import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FamlaServices, tenantBranding } from "../../services";
import { useAppSelector } from "../../stores";
import { AppLayout, HeaderLayout } from "../../layouts";
import { useHeaderLayoutCompanyData } from "../../hooks/useHeaderLayoutCompanyData";
import { getBrandingColor, getBrandingColorHover } from "../../utils/brandingStyles";
import { iAlert } from "../../interfaces";
import { Routes } from "../../routes";
import "../../features/process/process/process-create.css";

interface StateType {
  message: string;
  isLoading: boolean;
  name: string;
  email: string;
  subject: string;
  isSubmit: boolean;
  sendCopy: boolean;
}

const ContactUs = () => {
  const users = useAppSelector((state) => state.auth);
  const { t, i18n } = useTranslation();
  const headerProps = useHeaderLayoutCompanyData();

  const [state, setState] = React.useState<StateType>({
    isLoading: true,
    message: "",
    name: users?.user?.fullname || "",
    email: users?.user?.email || "",
    isSubmit: false,
    subject: "",
    sendCopy: false,
  });

  const [alert, setAlert] = React.useState<iAlert>({
    isActive: false,
    type: "",
    message: "",
  });

  const ORANGE = getBrandingColor();
  const ORANGE_DARK = getBrandingColorHover();

  const sInput: React.CSSProperties = {
    display: 'block',
    width: '100%',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 18,
    outline: 'none',
    transition: 'border-color .15s',
  };

  const sLabel: React.CSSProperties = {
    display: 'block',
    fontSize: 15,
    fontWeight: 500,
    color: 'var(--pc-label)',
    marginBottom: 6,
    lineHeight: 1.4,
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = ORANGE;
    e.currentTarget.style.boxShadow = `0 0 0 1px ${ORANGE}`;
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.removeProperty('border-color');
    e.currentTarget.style.removeProperty('box-shadow');
  };

  const handleChange = (event: any) => {
    const { type, name, value, checked } = event.target;
    const fieldValue = type === "checkbox" ? checked : value;
    setState({ ...state, [name]: fieldValue });
  };

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!state.email || !emailRegex.test(state.email)) {
      setAlert({
        isActive: true,
        type: "danger",
        message: t("views.legalDocs.contact.invalidEmail"),
      });
      return;
    }

    if (!state.subject.trim()) {
      setAlert({
        isActive: true,
        type: "danger",
        message: t("form.nameRequired"),
      });
      return;
    }

    setState({ ...state, isSubmit: true });
    const currentLanguage = i18n.language || 'en';
    const normalizedLanguage = currentLanguage.startsWith('fr') ? 'fr' : 'en';
    const formData = {
      name: state.name,
      email: state.email,
      subject: state.subject,
      message: state.message,
      sendCopy: state.sendCopy,
      language: normalizedLanguage,
    };

    const data = await FamlaServices(
      "api_host",
      `/contact_us/`,
      "POST",
      formData,
      users.access_token
    );

    if (data.status === 200) {
      setAlert({
        isActive: true,
        type: "success",
        message: t("views.legalDocs.contact.success"),
      });
      setState({
        ...state,
        subject: "",
        message: "",
        sendCopy: false,
        isSubmit: false,
      });
    } else {
      setAlert({
        isActive: true,
        type: "danger",
        message: t("views.legalDocs.contact.error"),
      });
    }
  };

  return (
    <AppLayout>
      <HeaderLayout {...headerProps} />
      <div className="pc-wrap" style={{ minHeight: '100vh', background: 'var(--pc-page-bg)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 24px' }}>
        <div style={{ width: '100%', maxWidth: 640 }}>
          {/* ── En-tête Navigation ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 }}>
            <Link
              to={Routes.HOMEVIEW}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, borderRadius: 8,
                background: 'var(--pc-surface)', border: '1px solid var(--pc-border)',
                color: 'var(--pc-label)', textDecoration: 'none', flexShrink: 0,
                transition: 'color .15s, border-color .15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--pc-back-hover-text)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--pc-border-hover)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--pc-label)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--pc-border)'; }}
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 500, color: 'var(--pc-title)' }}>
              {t('navigation.backHome')}
            </h1>
          </div>

          {/* ── Title & Subtitle ── */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 28, fontWeight: 600, color: 'var(--pc-title)' }}>
              {t("views.legalDocs.contact.gettingInTouch")}
            </h2>
            <p style={{ margin: '0 0 4px', fontSize: 15, color: 'var(--pc-label)', lineHeight: 1.5 }}>
              {t("views.legalDocs.contact.eitherFill")}
            </p>
            <p style={{ margin: 0, fontSize: 15, color: 'var(--pc-label)' }}>
              {tenantBranding.supportEmail}
            </p>
          </div>

          {/* ── Formulaire ── */}
          <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Champ Subject */}
            <div>
              <label style={sLabel}>
                {t('words.subject')}
                <span style={{ color: '#f87171', marginLeft: 2 }}>*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={state.subject}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={t('words.subject')}
                style={sInput}
              />
            </div>

            {/* Champ Message */}
            <div>
              <label style={sLabel}>
                {t('words.message')}
                <span style={{ color: '#f87171', marginLeft: 2 }}>*</span>
              </label>
              <textarea
                name="message"
                value={state.message || ''}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                rows={4}
                placeholder={t('words.message') + '...'}
                style={{ ...sInput, resize: 'none' }}
              />
            </div>

            {/* Checkbox Send Copy */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
              <input
                type="checkbox"
                id="sendCopy"
                name="sendCopy"
                checked={state.sendCopy}
                onChange={handleChange}
                style={{
                  width: 18,
                  height: 18,
                  accentColor: ORANGE,
                  cursor: 'pointer',
                  borderRadius: 4
                }}
              />
              <label
                htmlFor="sendCopy"
                style={{
                  fontSize: 15,
                  fontWeight: 400,
                  color: 'var(--pc-title)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  margin: 0
                }}
              >
                {t("views.legalDocs.contact.sendMeCopy")}
              </label>
            </div>

            {/* Alerte */}
            {alert.isActive && (
              <div
                role="alert"
                style={{
                  borderRadius: 8, padding: '12px 16px', fontSize: 15, fontWeight: 500,
                  background: alert.type === 'danger'
                    ? 'var(--pc-alert-danger-bg)'
                    : alert.type === 'warning'
                      ? 'var(--pc-alert-warning-bg)'
                      : alert.type === 'success'
                        ? 'var(--pc-orange-dim)'
                        : 'var(--pc-surface)',
                  border: `1px solid ${
                    alert.type === 'danger' ? '#f87171'
                    : alert.type === 'warning' ? '#fbbf24'
                    : ORANGE
                  }`,
                  color: alert.type === 'danger'
                    ? 'var(--pc-alert-danger-text)'
                    : alert.type === 'warning'
                      ? 'var(--pc-alert-warning-text)'
                      : ORANGE,
                }}
              >
                {alert.message}
              </div>
            )}

            {/* Bouton Submit */}
            <div>
              <button
                type="submit"
                disabled={state.isSubmit}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 10,
                  padding: '16px 24px', borderRadius: 12,
                  background: state.isSubmit ? ORANGE_DARK : ORANGE,
                  border: 'none', color: '#fff',
                  fontWeight: 600, fontSize: 19,
                  cursor: state.isSubmit ? 'not-allowed' : 'pointer',
                  opacity: state.isSubmit ? 0.7 : 1,
                  transition: 'background .15s, opacity .15s',
                }}
                onMouseEnter={e => { if (!state.isSubmit) (e.currentTarget as HTMLElement).style.background = ORANGE_DARK; }}
                onMouseLeave={e => { if (!state.isSubmit) (e.currentTarget as HTMLElement).style.background = ORANGE; }}
              >
                {state.isSubmit ? (
                  <>
                    <svg style={{ animation: 'spin 1s linear infinite', width: 16, height: 16 }} viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.25" />
                      <path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    {t('words.loading')}
                  </>
                ) : (
                  <>
                    {t('words.send')}
                    <svg
                      width={24}
                      height={24}
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden={true}
                      style={{ flexShrink: 0, marginLeft: 2 }}
                    >
                      <path
                        d="M5 12h14m0 0-5-5m5 5-5 5"
                        stroke="currentColor"
                        strokeWidth={2.4}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  );
};

export default ContactUs;

